import { useCallback, useState } from 'react';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { preflightPublishScenario, publishScenario } from './api';
import type { PublishPreflightResult } from './api';
import './usePublishConfirm.css';

interface UsePublishConfirmOptions {
  /** 公開成功時に呼ばれる (ローカル state の published 反映用) */
  onPublished?: (scenarioId: string) => void;
}

// 移行対象の種別ごとの表示行。always=true は 0 件でも常に表示する主要種別。
const MIGRATION_TYPE_ROWS: { key: string; label: string; always: boolean }[] = [
  { key: 'video', label: '動画', always: true },
  { key: 'image', label: '画像', always: false },
  { key: 'narration', label: 'ナレーション', always: true },
  { key: 'sound_logo', label: 'サウンドロゴ', always: false },
  { key: 'bgm', label: 'BGM', always: true },
  { key: 'storyboard', label: '絵コンテ', always: true },
];

/**
 * シナリオ公開の確認モーダルを共通化するフック。
 *
 * 公開ボタンの導線が複数あるため (シナリオブック / ワークスペース)、確認 UI と
 * 採用素材の共有移行ロジックをここに集約する。requestPublish() を呼ぶとモーダルが
 * 開き、ユーザーが「公開する」を押すと publishScenario({ migrateAssets }) を実行する。
 *
 * 非公開化 (unpublish) は確認不要なので呼び出し側で直接処理する。
 */
export function usePublishConfirm({ onPublished }: UsePublishConfirmOptions = {}) {
  const toast = useToast();
  const [target, setTarget] = useState<string | null>(null);
  const [migrateAssets, setMigrateAssets] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [preflight, setPreflight] = useState<PublishPreflightResult | null>(null);

  const requestPublish = useCallback(async (scenarioId: string) => {
    setTarget(scenarioId);
    setMigrateAssets(true);
    setPreflight(null);
    try {
      setPreflight(await preflightPublishScenario(scenarioId));
    } catch (err) {
      console.warn('Publish preflight failed:', err);
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!target) return;
    const scenarioId = target;
    setPublishing(true);
    try {
      const result = await publishScenario(scenarioId, { migrateAssets });
      onPublished?.(scenarioId);
      if (migrateAssets && result.migration) {
        const movedCount = result.migration.migrated.length + result.migration.recovered.length;
        const failedCount = result.migration.failed.length;
        if (failedCount > 0) {
          toast.error(`${movedCount}件を共有フォルダへ移行しました（${failedCount}件は後で自動的に再試行されます）`);
        } else if (movedCount > 0) {
          toast.success(`選択中の素材${movedCount}件を共有フォルダへ移行しました`);
        }
      }
      setTarget(null);
    } catch (err) {
      console.error('Failed to publish:', err);
      toast.error('共有に失敗しました');
    } finally {
      setPublishing(false);
    }
  }, [target, migrateAssets, onPublished, toast]);

  const dialog = (
    <ConfirmDialog
      isOpen={target !== null}
      title="このシナリオを組織内に共有しますか?"
      variant="info"
      confirmLabel="共有する"
      isLoading={publishing}
      onConfirm={handleConfirm}
      onCancel={() => { if (!publishing) setTarget(null); }}
      message={
        <div className="publish-confirm-body">
          <p>共有すると、同じ組織のメンバーがこのシナリオを閲覧できます。</p>
          <label className="publish-migrate-option">
            <input
              type="checkbox"
              checked={migrateAssets}
              disabled={publishing}
              onChange={(e) => setMigrateAssets(e.target.checked)}
            />
            <span>
              選択中の素材を共有フォルダへ移行する
              <small>
                選択中の素材も共有しないと、シナリオを共有しても組織の他のメンバーは素材を利用することができません。
              </small>
            </span>
          </label>
          {migrateAssets && preflight && (
            <div className="publish-preflight-info">
              <div className="publish-preflight-breakdown">
                {MIGRATION_TYPE_ROWS
                  .filter((row) => row.always || (preflight.breakdown?.[row.key] ?? 0) > 0)
                  .map((row) => (
                    <span key={row.key} className="publish-preflight-row">
                      <span className="publish-preflight-label">{row.label}</span>
                      <span className="publish-preflight-count">{preflight.breakdown?.[row.key] ?? 0}件</span>
                    </span>
                  ))}
              </div>
              {preflight.missing.length > 0 && (
                <span className="publish-preflight-warn">
                  ⚠ 削除済みの採用素材 {preflight.missing.length}件はスキップされます
                </span>
              )}
            </div>
          )}
        </div>
      }
    />
  );

  return { requestPublish, dialog, isPublishing: publishing };
}
